import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Select,
  Space,
  Typography,
  Spin,
  message,
  DatePicker,
  Table,
  Tag,
  Progress,
  Rate
} from 'antd';
import {
  DownloadOutlined,
  ReloadOutlined,
  LineChartOutlined,
  BarChartOutlined,
  PieChartOutlined,
  RiseOutlined,
  FallOutlined,
  TeamOutlined,
  UserOutlined,
  ToolOutlined
} from '@ant-design/icons';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { analyticsService, type GlobalStats } from '../services/analyticsService';
import { useAuth } from '../contexts/AuthContext';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Enregistrer les composants Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  ChartTitle,
  Tooltip,
  Legend,
  Filler
);

const AnalyticsPage: React.FC = () => {
  const { currentUser } = useAuth();
  // Seul le Super Admin a accès aux données financières (CA, commandes, graphiques revenus) selon acces_dashboard.md
  const canViewRevenue = currentUser?.role === 'super_admin';

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<GlobalStats | null>(null);
  const [period, setPeriod] = useState(6);
  const [selectedItem, setSelectedItem] = useState<string>('all');
  const [exportLoading, setExportLoading] = useState(false);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

  // Données des graphiques
  const [interventionsTimeline, setInterventionsTimeline] = useState<any>(null);
  const [revenueTimeline, setRevenueTimeline] = useState<any>(null);
  const [interventionsByType, setInterventionsByType] = useState<any>(null);
  const [customerSatisfaction, setCustomerSatisfaction] = useState<any>(null);
  const [technicianPerformance, setTechnicianPerformance] = useState<any[]>([]);
  const [topProducts, setTopProducts] = useState<any>(null);

  const loadAnalytics = useCallback(async () => {
    try {
      setLoading(true);

      // Préparer les paramètres de date
      const params: any = {};
      if (dateRange) {
        params.startDate = dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateRange[1].format('YYYY-MM-DD');
      }

      const chartParams = {
        period,
        startDate: params.startDate,
        endDate: params.endDate,
      };

      // Charger les statistiques globales et les graphiques en parallèle
      const [statsData, timelineData, revenueData, typeData, satisfactionData, techData, productsData] = await Promise.all([
        analyticsService.getGlobalStats(params.startDate, params.endDate),
        analyticsService.getChartData('interventions-timeline', chartParams),
        canViewRevenue ? analyticsService.getChartData('revenue-timeline', chartParams) : Promise.resolve(null),
        analyticsService.getChartData('interventions-by-type', chartParams),
        analyticsService.getChartData('customer-satisfaction', chartParams),
        analyticsService.getTechnicianPerformance(params.startDate, params.endDate),
        canViewRevenue ? analyticsService.getChartData('top-products', chartParams) : Promise.resolve(null)
      ]);

      setStats(statsData);
      setInterventionsTimeline(timelineData);
      setRevenueTimeline(revenueData);
      setInterventionsByType(typeData);
      setCustomerSatisfaction(satisfactionData);
      setTechnicianPerformance(techData);
      setTopProducts(productsData);
    } catch (error) {
      console.error('Erreur chargement analytics:', error);
      message.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  }, [dateRange, period, canViewRevenue]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const handleExport = async (format: 'excel' | 'pdf') => {
    try {
      setExportLoading(true);
      message.loading(`Génération du rapport ${format.toUpperCase()}...`);
      
      const startDate = dateRange ? dateRange[0].format('YYYY-MM-DD') : undefined;
      const endDate = dateRange ? dateRange[1].format('YYYY-MM-DD') : undefined;
      const blob = format === 'excel'
        ? await analyticsService.exportToExcel(undefined, startDate, endDate)
        : await analyticsService.exportToPDF(undefined, startDate, endDate);

      const filename = `rapport_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      analyticsService.downloadFile(blob, filename);
      
      message.success(`Rapport ${format.toUpperCase()} téléchargé avec succès`);
    } catch (error) {
      console.error('Erreur export:', error);
      message.error('Erreur lors de l\'export');
    } finally {
      setExportLoading(false);
    }
  };

  // Configuration graphique timeline interventions
  const interventionsChartData = interventionsTimeline ? {
    labels: interventionsTimeline.data.map((d: any) => new Date(d.date).toLocaleDateString('fr-FR')),
    datasets: [{
      label: 'Interventions',
      data: interventionsTimeline.data.map((d: any) => d.count),
      borderColor: '#0a543d',
      backgroundColor: 'rgba(10, 84, 61, 0.1)',
      tension: 0.4,
      fill: true
    }]
  } : null;

  // Configuration graphique revenue
  const revenueChartData = revenueTimeline ? {
    labels: revenueTimeline.data.map((d: any) => new Date(d.date).toLocaleDateString('fr-FR')),
    datasets: [{
      label: 'Chiffre d\'affaires (FCFA)',
      data: revenueTimeline.data.map((d: any) => d.revenue || 0),
      borderColor: '#2196f3',
      backgroundColor: 'rgba(33, 150, 243, 0.1)',
      tension: 0.4,
      fill: true
    }]
  } : null;

  // Configuration graphique par type
  const typeChartData = interventionsByType ? {
    labels: interventionsByType.data.map((d: any) => d.intervention_type || 'Non spécifié'),
    datasets: [{
      label: 'Nombre d\'interventions',
      data: interventionsByType.data.map((d: any) => d.count),
      backgroundColor: [
        '#0a543d',
        '#2196f3',
        '#ff9800',
        '#f44336',
        '#9c27b0',
        '#4caf50'
      ]
    }]
  } : null;

  // Configuration graphique satisfaction
  const satisfactionChartData = customerSatisfaction ? {
    labels: customerSatisfaction.data.map((d: any) => d.month),
    datasets: [{
      label: 'Note moyenne',
      data: customerSatisfaction.data.map((d: any) => parseFloat(d.avgRating)),
      borderColor: '#4caf50',
      backgroundColor: 'rgba(76, 175, 80, 0.1)',
      tension: 0.4,
      fill: true
    }]
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" tip="Chargement des statistiques..." />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* En-tête */}
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
            📊 Analytics & Reporting
          </Title>
          <Text type="secondary" style={{ fontSize: '14px' }}>
            Statistiques détaillées et rapports d'activité
          </Text>
        </Col>
        <Col>
          <Space size="middle" wrap>
            <Select
              value={!canViewRevenue && selectedItem === 'revenue' ? 'all' : selectedItem}
              onChange={setSelectedItem}
              style={{ minWidth: 200 }}
              options={[
                { value: 'all', label: '📊 Toutes les métriques' },
                { value: 'interventions', label: '🛠️ Interventions' },
                ...(canViewRevenue ? [{ value: 'revenue', label: '💰 Commandes & CA' }] : []),
                { value: 'quotes', label: '📄 Devis & Conversion' },
                { value: 'complaints', label: '📢 Réclamations & Satisfaction' },
                { value: 'technicians', label: '👨‍🔧 Performance Techniciens' },
              ]}
            />
            <RangePicker
              format="DD/MM/YYYY"
              placeholder={['Date début', 'Date fin']}
              onChange={(dates) => setDateRange(dates as any)}
              style={{ width: 260 }}
            />
            <Select
              value={period}
              onChange={setPeriod}
              style={{ width: 110 }}
              options={[
                { value: 3, label: '3 mois' },
                { value: 6, label: '6 mois' },
                { value: 12, label: '12 mois' }
              ]}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={loadAnalytics}
              loading={loading}
            >
              Actualiser
            </Button>
            <Button
              type="default"
              icon={<DownloadOutlined />}
              onClick={() => handleExport('excel')}
              loading={exportLoading}
            >
              Excel
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => handleExport('pdf')}
              loading={exportLoading}
              style={{ 
                background: '#0a543d',
                borderColor: '#0a543d'
              }}
            >
              PDF
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Statistiques globales */}
      {stats && (
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          {(selectedItem === 'all' || selectedItem === 'interventions') && (
            <Col xs={24} sm={12} md={selectedItem === 'interventions' ? 24 : (canViewRevenue ? 6 : 8)}>
              <Card>
                <Statistic
                  title="Total Interventions"
                  value={stats.interventions.total || 0}
                  prefix="🛠️"
                  suffix={
                    <Text type="success" style={{ fontSize: '14px' }}>
                      {stats.interventions.completed || 0} complétées
                    </Text>
                  }
                />
              </Card>
            </Col>
          )}
          
          {canViewRevenue && selectedItem === 'revenue' && (
            <>
              <Col xs={24} sm={8} md={8}>
                <Card>
                  <Statistic
                    title="Chiffre d'Affaires Brut"
                    value={stats.revenue?.gross ?? stats.orders.grossRevenue ?? stats.orders.revenue ?? 0}
                    precision={0}
                    prefix="📈"
                    suffix="FCFA"
                    valueStyle={{ color: '#1890ff', fontWeight: 600 }}
                  />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {(stats.orders.totalPayments ?? stats.orders.total) || 0} transaction{((stats.orders.totalPayments ?? stats.orders.total) || 0) > 1 ? 's' : ''} encaissée{((stats.orders.totalPayments ?? stats.orders.total) || 0) > 1 ? 's' : ''}
                  </Text>
                </Card>
              </Col>
              <Col xs={24} sm={8} md={8}>
                <Card>
                  <Statistic
                    title="Remboursements (Mobile Money)"
                    value={stats.revenue?.refunds ?? stats.orders.totalRefunds ?? 0}
                    precision={0}
                    prefix="↩️"
                    suffix="FCFA"
                    valueStyle={{ color: (stats.revenue?.refunds || stats.orders.totalRefunds) ? '#cf1322' : '#8c8c8c', fontWeight: 600 }}
                  />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {(stats.revenue?.refundsCount ?? stats.orders.refundsCount ?? 0)} remboursement{((stats.revenue?.refundsCount ?? stats.orders.refundsCount ?? 0)) > 1 ? 's' : ''} effectué{((stats.revenue?.refundsCount ?? stats.orders.refundsCount ?? 0)) > 1 ? 's' : ''}
                  </Text>
                </Card>
              </Col>
              <Col xs={24} sm={8} md={8}>
                <Card>
                  <Statistic
                    title="Chiffre d'Affaires Net"
                    value={stats.revenue?.net ?? stats.orders.netRevenue ?? stats.orders.revenue ?? 0}
                    precision={0}
                    prefix="💰"
                    suffix="FCFA"
                    valueStyle={{ color: '#0a543d', fontWeight: 700 }}
                  />
                  <Text type="success" style={{ fontSize: '12px' }}>
                    Revenu net encaissé après déductions
                  </Text>
                </Card>
              </Col>
            </>
          )}

          {canViewRevenue && selectedItem === 'all' && (
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Chiffre d'Affaires Net"
                  value={stats.revenue?.net ?? stats.orders.netRevenue ?? stats.orders.revenue ?? 0}
                  precision={0}
                  prefix="💰"
                  suffix="FCFA"
                  valueStyle={{ color: '#0a543d', fontWeight: 600 }}
                />
                <div style={{ marginTop: 2, display: 'flex', flexDirection: 'column' }}>
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    {(stats.orders.totalPayments ?? stats.orders.total) || 0} transaction{((stats.orders.totalPayments ?? stats.orders.total) || 0) > 1 ? 's' : ''}
                    {(stats.revenue?.gross || stats.orders.grossRevenue) ? ` • Brut: ${Math.round(Number(stats.revenue?.gross ?? stats.orders.grossRevenue)).toLocaleString('fr-FR')} F` : ''}
                  </Text>
                  {(stats.revenue?.refunds || stats.orders.totalRefunds) ? (
                    <Text type="danger" style={{ fontSize: '11px', fontWeight: 500 }}>
                      Remboursé: -{Math.round(Number(stats.revenue?.refunds ?? stats.orders.totalRefunds)).toLocaleString('fr-FR')} FCFA ({stats.revenue?.refundsCount ?? stats.orders.refundsCount ?? 0})
                    </Text>
                  ) : null}
                </div>
              </Card>
            </Col>
          )}

          {(selectedItem === 'all' || selectedItem === 'quotes') && (
            <Col xs={24} sm={12} md={selectedItem === 'quotes' ? 24 : (canViewRevenue ? 6 : 8)}>
              <Card>
                <Statistic
                  title="Taux Conversion Devis"
                  value={parseFloat(stats.quotes.conversionRate || '0')}
                  precision={1}
                  suffix="%"
                  prefix={parseFloat(stats.quotes.conversionRate || '0') >= 50 ? <RiseOutlined /> : <FallOutlined />}
                  valueStyle={{ 
                    color: parseFloat(stats.quotes.conversionRate || '0') >= 50 ? '#3f8600' : '#cf1322' 
                  }}
                />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {stats.quotes.accepted || 0}/{stats.quotes.total || 0} acceptés
                </Text>
              </Card>
            </Col>
          )}

          {(selectedItem === 'all' || selectedItem === 'complaints') && (
            <Col xs={24} sm={12} md={selectedItem === 'complaints' ? 24 : (canViewRevenue ? 6 : 8)}>
              <Card>
                <Statistic
                  title="Réclamations"
                  value={stats.complaints.total || 0}
                  prefix="📢"
                  suffix={
                    <Text type="warning" style={{ fontSize: '14px' }}>
                      {stats.complaints.open || 0} en cours
                    </Text>
                  }
                />
              </Card>
            </Col>
          )}
        </Row>
      )}

      {/* Graphiques */}
      <Row gutter={[16, 16]}>
        {/* Timeline Interventions */}
        {(selectedItem === 'all' || selectedItem === 'interventions') && (
          <Col xs={24} lg={selectedItem === 'all' ? 12 : 24}>
            <Card 
              title={
                <Space>
                  <LineChartOutlined style={{ color: '#0a543d' }} />
                  <span>Évolution des Interventions</span>
                </Space>
              }
              style={{ height: '400px', marginBottom: 16 }}
            >
              <div style={{ height: '300px' }}>
                {interventionsChartData ? (
                  <Line data={interventionsChartData} options={chartOptions} />
                ) : (
                  <div style={{ textAlign: 'center', paddingTop: '100px' }}>
                    <Text type="secondary">Aucune donnée disponible</Text>
                  </div>
                )}
              </div>
            </Card>
          </Col>
        )}

        {/* Timeline Revenue */}
        {canViewRevenue && (selectedItem === 'all' || selectedItem === 'revenue') && (
          <Col xs={24} lg={selectedItem === 'all' ? 12 : 24}>
            <Card 
              title={
                <Space>
                  <BarChartOutlined style={{ color: '#2196f3' }} />
                  <span>Évolution du Chiffre d'Affaires</span>
                </Space>
              }
              style={{ height: '400px', marginBottom: 16 }}
            >
              <div style={{ height: '300px' }}>
                {revenueChartData ? (
                  <Line data={revenueChartData} options={chartOptions} />
                ) : (
                  <div style={{ textAlign: 'center', paddingTop: '100px' }}>
                    <Text type="secondary">Aucune donnée disponible</Text>
                  </div>
                )}
              </div>
            </Card>
          </Col>
        )}

        {/* Répartition par type */}
        {(selectedItem === 'all' || selectedItem === 'interventions') && (
          <Col xs={24} lg={selectedItem === 'all' ? 12 : 24}>
            <Card 
              title={
                <Space>
                  <PieChartOutlined style={{ color: '#ff9800' }} />
                  <span>Répartition par Type</span>
                </Space>
              }
              style={{ height: '400px', marginBottom: 16 }}
            >
              <div style={{ height: '300px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {typeChartData && typeChartData.labels.length > 0 ? (
                  <Doughnut data={typeChartData} options={{ ...chartOptions, scales: undefined }} />
                ) : (
                  <Text type="secondary">Aucune donnée disponible</Text>
                )}
              </div>
            </Card>
          </Col>
        )}

        {/* Satisfaction Client */}
        {(selectedItem === 'all' || selectedItem === 'complaints') && (
          <Col xs={24} lg={selectedItem === 'all' ? 12 : 24}>
            <Card 
              title={
                <Space>
                  <LineChartOutlined style={{ color: '#4caf50' }} />
                  <span>Satisfaction Client (Moyenne)</span>
                </Space>
              }
              style={{ height: '400px', marginBottom: 16 }}
            >
              <div style={{ height: '300px' }}>
                {satisfactionChartData && satisfactionChartData.labels.length > 0 ? (
                  <Bar data={satisfactionChartData} options={{ ...chartOptions, scales: { y: { min: 0, max: 5 } } }} />
                ) : (
                  <div style={{ textAlign: 'center', paddingTop: '100px' }}>
                    <Text type="secondary">Aucune donnée disponible</Text>
                  </div>
                )}
              </div>
            </Card>
          </Col>
        )}
      </Row>

      {/* Statistiques Utilisateurs + Techniciens */}
      {stats && (selectedItem === 'all' || selectedItem === 'technicians') && (
        <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Total Utilisateurs"
                value={stats.users?.total || 0}
                prefix={<TeamOutlined />}
                valueStyle={{ color: '#0a543d' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Techniciens"
                value={stats.users?.technicians || 0}
                prefix={<ToolOutlined />}
                valueStyle={{ color: '#2196f3' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Clients"
                value={stats.users?.customers || 0}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#ff9800' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Répartition par statut des interventions */}
      {stats && stats.interventions.byStatus && stats.interventions.byStatus.length > 0 && (selectedItem === 'all' || selectedItem === 'interventions') && (
        <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
          <Col xs={24}>
            <Card title={<Space><PieChartOutlined style={{ color: '#0a543d' }} /><span>Répartition par Statut</span></Space>}>
              <Row gutter={[16, 8]}>
                {stats.interventions.byStatus.map((item) => {
                  const statusColors: Record<string, string> = {
                    pending: '#ff9800', assigned: '#2196f3', in_progress: '#1890ff',
                    completed: '#4caf50', cancelled: '#f44336', scheduled: '#9c27b0',
                    execution_confirmed: '#00bcd4', diagnostic_submitted: '#13c2c2',
                    arrived: '#2f54eb', on_the_way: '#1890ff', accepted: '#52c41a',
                    client_unreachable: '#fa541c'
                  };
                  const statusLabels: Record<string, string> = {
                    pending: 'En attente', assigned: 'Assignée', in_progress: 'En cours',
                    completed: 'Terminée', cancelled: 'Annulée', scheduled: 'Planifiée',
                    execution_confirmed: 'Exécution confirmée', diagnostic_submitted: 'Diagnostic soumis',
                    arrived: 'Arrivé sur site', on_the_way: 'En route', accepted: 'Acceptée',
                    client_unreachable: 'Client injoignable'
                  };
                  const total = stats.interventions.total || 1;
                  const pct = Math.round((parseInt(String(item.count), 10) / total) * 100);
                  return (
                    <Col xs={12} sm={8} md={4} key={item.status}>
                      <div style={{ textAlign: 'center' }}>
                        <Progress
                          type="circle"
                          percent={pct}
                          size={80}
                          strokeColor={statusColors[item.status] || '#999'}
                          format={() => item.count}
                        />
                        <div style={{ marginTop: 8 }}>
                          <Tag color={statusColors[item.status] || 'default'}>
                            {statusLabels[item.status] || item.status.replace(/_/g, ' ')}
                          </Tag>
                        </div>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </Card>
          </Col>
        </Row>
      )}

      {/* Top Produits */}
      {canViewRevenue && topProducts && topProducts.data && topProducts.data.length > 0 && (selectedItem === 'all' || selectedItem === 'revenue') && (
        <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
          <Col xs={24}>
            <Card title={<Space><BarChartOutlined style={{ color: '#9c27b0' }} /><span>Top Produits</span></Space>}>
              <div style={{ height: '300px' }}>
                <Bar
                  data={{
                    labels: topProducts.data.map((d: any) => d.nom || d.name || 'N/A'),
                    datasets: [{
                      label: 'Quantité vendue',
                      data: topProducts.data.map((d: any) => parseInt(d.total_sold, 10) || 0),
                      backgroundColor: '#9c27b0',
                    }]
                  }}
                  options={{
                    ...chartOptions,
                    indexAxis: 'y' as const,
                  }}
                />
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* Performance Techniciens */}
      {technicianPerformance && technicianPerformance.length > 0 && (selectedItem === 'all' || selectedItem === 'technicians') && (
        <Row gutter={[16, 16]} style={{ marginTop: '24px', marginBottom: '24px' }}>
          <Col xs={24}>
            <Card title={<Space><TeamOutlined style={{ color: '#0a543d' }} /><span>Performance des Techniciens</span></Space>}>
              <Table
                dataSource={technicianPerformance}
                rowKey="technicianId"
                pagination={{ pageSize: 10 }}
                columns={[
                  { title: 'Technicien', dataIndex: 'name', key: 'name' },
                  { title: 'Email', dataIndex: 'email', key: 'email', responsive: ['md'] as any },
                  { title: 'Spécialité', dataIndex: 'specialty', key: 'specialty', responsive: ['lg'] as any,
                    render: (val: string) => val ? <Tag color="blue">{val}</Tag> : <Text type="secondary">-</Text>
                  },
                  { title: 'Interventions', dataIndex: 'totalInterventions', key: 'total', align: 'center' as const },
                  { title: 'Complétées', dataIndex: 'completedInterventions', key: 'completed', align: 'center' as const,
                    render: (val: number, record: any) => (
                      <Space>
                        <span>{val}</span>
                        <Progress percent={Math.round(record.completionRate)} size="small" style={{ width: 60 }} />
                      </Space>
                    )
                  },
                  { title: 'Note', dataIndex: 'avgRating', key: 'rating', align: 'center' as const,
                    render: (val: string) => {
                      const rating = parseFloat(val);
                      return rating > 0 ? <Rate disabled defaultValue={rating} allowHalf style={{ fontSize: 14 }} /> : <Text type="secondary">-</Text>;
                    }
                  },
                ]}
              />
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default AnalyticsPage;
